/**
 * Caption helpers used by Media Studio chips AND (2026-07-09, curate-media
 * batch flow) the async caption backfill in `CreateDraftsFromMediaService.ts`.
 * POSTs the asset to `/api/content/caption` (a Gemini vision wrapper).
 * Caller owns the failure path (toast on throw for the single-caption path;
 * best-effort swallow for the batch backfill path).
 *
 * Auth: the endpoint requires a Bearer token. Caller MUST pass `accessToken`
 * (read from `useAuth().session?.access_token`). Without it the endpoint
 * returns 401.
 */

/**
 * Batch client: ONE image fetch + ONE Gemini vision pass returning a caption
 * per requested platform, brand-voice-routed via `projectId`/`brandKitId`.
 */
export async function suggestCaptionsBatch(args: {
  assetUrl: string;
  coverImageUrl?: string | null;
  kind?: 'image' | 'video';
  platforms: string[];
  projectId?: string | null;
  brandKitId?: string | null;
  accessToken: string;
}): Promise<Record<string, string>> {
  const {
    assetUrl,
    coverImageUrl = null,
    kind = 'image',
    platforms,
    projectId,
    brandKitId,
    accessToken,
  } = args;
  if (!accessToken) {
    throw new Error('Caption suggest requires an access token');
  }
  if (!platforms || platforms.length === 0) {
    throw new Error('Caption suggest requires at least one platform');
  }
  const response = await fetch('/api/content/caption', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ assetUrl, coverImageUrl, kind, platforms, projectId, brandKitId }),
  });
  if (!response.ok) {
    throw new Error(`Caption suggest failed: ${response.status} ${response.statusText}`);
  }
  const data = (await response.json()) as { captions?: Record<string, string> };
  return data.captions ?? {};
}

/**
 * Single-platform thin wrapper over `suggestCaptionsBatch`, kept for the
 * Media Studio inline caption chip.
 */
export async function suggestCaption(args: {
  assetUrl: string;
  accessToken: string;
  platform?: 'instagram' | 'tiktok' | 'twitter' | 'linkedin' | 'youtube';
  kind?: 'image' | 'video';
}): Promise<string> {
  const { assetUrl, accessToken, platform = 'instagram', kind = 'image' } = args;
  const captions = await suggestCaptionsBatch({
    assetUrl,
    kind,
    platforms: [platform],
    accessToken,
  });
  const caption = captions[platform];
  if (typeof caption !== 'string' || caption.length === 0) {
    throw new Error('Caption suggest returned empty response');
  }
  return caption;
}
